import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { MAPA_CARGOS } from '@/components/Configuracao/mapaCargos/mapaCargosData'
import {
  cloneMapaCargoSecoes,
  mergeMapaCargos,
  resolveMapaCargo,
  type MapaCargoOverridesMap,
} from '@/components/Configuracao/mapaCargos/mapaCargosMerge'
import type { MapaCargo, MapaCargoSecao } from '@/components/Configuracao/mapaCargos/mapaCargosTypes'
import { mapaCargosService } from '@/services/mapaCargosService'

export function useMapaCargosOverrides() {
  const [overrides, setOverrides] = useState<MapaCargoOverridesMap>({})
  const [loading, setLoading] = useState(true)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const next = await mapaCargosService.listOverrides()
      setOverrides(next)
    } catch {
      toast.error('Não foi possível carregar personalizações do mapa de cargos.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const cargos = mergeMapaCargos(MAPA_CARGOS, overrides)

  const resolveCargo = useCallback(
    (id: string | null | undefined) => (id ? resolveMapaCargo(id, MAPA_CARGOS, overrides) : undefined),
    [overrides],
  )

  const saveEscopo = useCallback(
    async (cargoId: string, secoes: MapaCargoSecao[]) => {
      const saved = await mapaCargosService.saveEscopo(cargoId, secoes)
      setOverrides((prev) => ({ ...prev, [cargoId]: saved?.secoes ?? secoes }))
    },
    [],
  )

  const resetEscopo = useCallback(async (cargoId: string) => {
    await mapaCargosService.resetEscopo(cargoId)
    setOverrides((prev) => {
      const next = { ...prev }
      delete next[cargoId]
      return next
    })
  }, [])

  return {
    overrides,
    cargos,
    loading,
    reload,
    resolveCargo,
    saveEscopo,
    resetEscopo,
  }
}

export function useMapaCargosEscopoEdit(params: {
  cargo: MapaCargo | null
  hasOverride: boolean
  onSave: (secoes: MapaCargoSecao[]) => Promise<void>
  onReset: () => Promise<void>
}) {
  const { cargo, hasOverride, onSave, onReset } = params
  const [isEditing, setIsEditing] = useState(false)
  const [draftSecoes, setDraftSecoes] = useState<MapaCargoSecao[]>([])
  const [saving, setSaving] = useState(false)

  const startEdit = useCallback(() => {
    if (!cargo) return
    setDraftSecoes(cloneMapaCargoSecoes(cargo.secoes))
    setIsEditing(true)
  }, [cargo])

  const cancelEdit = useCallback(() => {
    setIsEditing(false)
    setDraftSecoes([])
  }, [])

  const saveEdit = useCallback(async () => {
    const sanitized = draftSecoes
      .map((secao) => ({
        ...secao,
        titulo: secao.titulo.trim(),
        atividades: secao.atividades.map((item) => item.trim()).filter(Boolean),
      }))
      .filter((secao) => secao.titulo && secao.atividades.length > 0)

    if (sanitized.length === 0) {
      toast.error('Informe ao menos um bloco com título e atividades.')
      return
    }

    setSaving(true)
    try {
      await onSave(sanitized)
      setIsEditing(false)
      toast.success('Escopo salvo')
    } catch {
      toast.error('Não foi possível salvar o escopo.')
    } finally {
      setSaving(false)
    }
  }, [draftSecoes, onSave])

  const resetToDefault = useCallback(async () => {
    setSaving(true)
    try {
      await onReset()
      setIsEditing(false)
      toast.success('Escopo restaurado para o padrão')
    } catch {
      toast.error('Não foi possível restaurar o escopo.')
    } finally {
      setSaving(false)
    }
  }, [onReset])

  return {
    isEditing,
    draftSecoes,
    setDraftSecoes,
    saving,
    hasOverride,
    startEdit,
    cancelEdit,
    saveEdit,
    resetToDefault,
  }
}
